import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Linkedin, Youtube, Mail, Globe } from "lucide-react";
import { ESS_LOGO, FOOTER_DISCLAIMER, SOCIAL_LINKS, ETSY_URL } from "@/lib/brand";

const TikTokIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.69a8.16 8.16 0 0 0 4.78 1.52V6.76a4.85 4.85 0 0 1-1.85-.07Z" />
  </svg>
);

const linkCls =
  "text-[13px] tracking-wide text-white/70 hover:text-ess-gold transition-colors";

export const Footer = () => {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="ess-footer"
      className="bg-ess-emerald text-white relative overflow-hidden"
    >
      <div className="absolute inset-0 ess-grain opacity-30 pointer-events-none" />
      <div className="ess-container relative py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3">
              <img
                src={ESS_LOGO}
                alt="ESS"
                className="h-14 w-14 object-contain bg-white/5 p-1 rounded-full"
              />
              <div>
                <p className="font-display text-2xl">The Exit Strategy Society</p>
                <p className="ess-overline text-[9px] text-ess-gold">
                  Design the Life You Deserve
                </p>
              </div>
            </div>
            <p className="mt-6 text-white/75 max-w-md leading-relaxed">
              Helping busy professionals reclaim their time, build their freedom,
              and design their next chapter.
            </p>
            <div className="flex items-center gap-4 mt-8">
              <a
                data-testid="footer-social-facebook"
                href={SOCIAL_LINKS.facebook}
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-ess-gold hover:text-ess-gold transition"
                aria-label="Facebook"
              >
                <Facebook size={16} />
              </a>
              <a
                data-testid="footer-social-instagram"
                href={SOCIAL_LINKS.instagram}
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-ess-gold hover:text-ess-gold transition"
                aria-label="Instagram"
              >
                <Instagram size={16} />
              </a>
              <a
                data-testid="footer-social-linkedin"
                href={SOCIAL_LINKS.linkedin}
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-ess-gold hover:text-ess-gold transition"
                aria-label="LinkedIn"
              >
                <Linkedin size={16} />
              </a>
              <a
                data-testid="footer-social-tiktok"
                href={SOCIAL_LINKS.tiktok}
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-ess-gold hover:text-ess-gold transition"
                aria-label="TikTok"
              >
                <TikTokIcon />
              </a>
              <a
                data-testid="footer-social-youtube"
                href={SOCIAL_LINKS.youtube}
                target="_blank"
                rel="noreferrer"
                className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-ess-gold hover:text-ess-gold transition"
                aria-label="YouTube"
              >
                <Youtube size={16} />
              </a>
            </div>
          </div>

          <div className="md:col-span-3">
            <p className="ess-overline text-ess-gold mb-5">Explore</p>
            <ul className="space-y-3">
              <li><Link className={linkCls} to="/about">About</Link></li>
              <li><Link className={linkCls} to="/services">Services</Link></li>
              <li><Link className={linkCls} to="/digital-products">Digital Products</Link></li>
              <li><Link className={linkCls} to="/resources">Resources</Link></li>
              <li><Link className={linkCls} to="/impact">Impact</Link></li>
              <li><Link className={linkCls} to="/partnerships">Partnerships</Link></li>
              <li>
                <a
                  className={linkCls}
                  href={ETSY_URL}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-boutique-link"
                >
                  Boutique
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="ess-overline text-ess-gold mb-5">Legal</p>
            <ul className="space-y-3">
              <li><Link className={linkCls} to="/legal/privacy">Privacy Policy</Link></li>
              <li><Link className={linkCls} to="/legal/terms">Terms & Conditions</Link></li>
              <li><Link className={linkCls} to="/legal/disclaimer">Disclaimer</Link></li>
              <li><Link className={linkCls} to="/legal/accessibility">Accessibility</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <p className="ess-overline text-ess-gold mb-5">Contact</p>
            <ul className="space-y-3 text-[13px] text-white/75">
              <li className="flex items-start gap-2">
                <Mail size={14} className="mt-1 text-ess-gold" />
                <a
                  href="mailto:exitstrategysociety@gmail.com"
                  className="hover:text-ess-gold break-all"
                  data-testid="footer-email"
                >
                  exitstrategysociety@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Globe size={14} className="mt-1 text-ess-gold" />
                <a
                  href="https://exitstrategysociety.com"
                  className="hover:text-ess-gold"
                  data-testid="footer-website"
                >
                  exitstrategysociety.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 text-[12px] text-white/55 leading-relaxed">
          <p className="max-w-4xl">{FOOTER_DISCLAIMER}</p>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
            <p>© {year} The Exit Strategy Society. All rights reserved.</p>
            <p className="text-white/45">
              The Exit Strategy Society is a DBA of 4ever Wellness LLC.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
